import type { Source } from "./source";
import type { GradientAxis, GradientStop } from "./gradient";

/**
 * The sequencer's pure model (no React, no three). A timeline is an alternating
 * strip of scenes and the transitions between them:
 *
 *   scene0 ──trans0──▶ scene1 ──trans1──▶ scene2 ...
 *
 * Each scene holds a source for `hold` seconds; each transition morphs the swarm
 * to the next scene over `duration` seconds using a named effect. `sampleAt`
 * turns a wall-clock time into "what should the swarm be doing right now", which
 * is all the renderer (SequencerField) needs to play OR scrub.
 */

export type TransitionKind = "morph" | "explode" | "implode" | "spiral" | "fade" | "fountain";

export type TransitionDef = {
  kind: TransitionKind;
  label: string;
  glyph: string;
  /** shader effect id (drone.vert.glsl) */
  code: number;
  /** effect strength passed to the shader */
  amt: number;
  blurb: string;
};

/** The draggable palette of transition presets — the swarm "does a thing" mid-morph. */
export const TRANSITIONS: TransitionDef[] = [
  { kind: "morph", label: "Morph", glyph: "→", code: 0, amt: 0, blurb: "direct staggered ease" },
  { kind: "explode", label: "Explode", glyph: "✺", code: 1, amt: 5, blurb: "burst outward, then reform" },
  { kind: "implode", label: "Implode", glyph: "◍", code: 2, amt: 0, blurb: "collapse through the centre" },
  { kind: "spiral", label: "Spiral", glyph: "❋", code: 3, amt: 6.283, blurb: "swirl a full turn" },
  { kind: "fade", label: "Fade", glyph: "☼", code: 4, amt: 0, blurb: "dim out then back in" },
  { kind: "fountain", label: "Fountain", glyph: "⤒", code: 5, amt: 4, blurb: "arc up and fall in" },
];

const BY_KIND = new Map(TRANSITIONS.map((t) => [t.kind, t]));
export const transDef = (k: TransitionKind): TransitionDef => BY_KIND.get(k) ?? TRANSITIONS[0];

/**
 * A scene's LOOK — how it's coloured, baked into its cached cloud. "source"
 * keeps the shape's native colours (rainbow/photo); solid and gradient mirror
 * the studio's Color section, so "capture" carries the whole look over.
 */
export type SceneLook =
  | { color: "source" }
  | { color: "solid"; hex: string }
  | { color: "gradient"; stops: GradientStop[]; axis: GradientAxis };

export const DEFAULT_LOOK: SceneLook = { color: "source" };

export type Scene = { id: string; source: Source; hold: number; look: SceneLook };
export type Transition = { kind: TransitionKind; duration: number };
/** `transitions[i]` joins `scenes[i]` → `scenes[i+1]`, so length is scenes-1. */
export type Timeline = { scenes: Scene[]; transitions: Transition[] };

let _idc = 0;
export const newSceneId = () => "sc" + ++_idc;
export const defaultScene = (source: Source, hold = 3, look: SceneLook = DEFAULT_LOOK): Scene =>
  ({ id: newSceneId(), source, hold, look });
export const defaultTransition = (): Transition => ({ kind: "morph", duration: 1.5 });

/** A short badge for a scene's look ("" for native colours). */
export function lookLabel(look: SceneLook | undefined): string {
  if (!look || look.color === "source") return "";
  return look.color === "solid" ? look.hex : "gradient";
}

export type Segment =
  | { type: "hold"; index: number; start: number; end: number }
  | { type: "trans"; from: number; to: number; kind: TransitionKind; start: number; end: number };

/** Lay the timeline out on a wall-clock axis: alternating hold/trans segments. */
export function layout(tl: Timeline): { segments: Segment[]; total: number } {
  const segments: Segment[] = [];
  let t = 0;
  for (let i = 0; i < tl.scenes.length; i++) {
    const hold = Math.max(0, tl.scenes[i].hold);
    segments.push({ type: "hold", index: i, start: t, end: t + hold });
    t += hold;
    if (i < tl.scenes.length - 1) {
      const tr = tl.transitions[i] ?? defaultTransition();
      const dur = Math.max(0.05, tr.duration);
      segments.push({ type: "trans", from: i, to: i + 1, kind: tr.kind, start: t, end: t + dur });
      t += dur;
    }
  }
  return { segments, total: t };
}

export type PlayState =
  | { kind: "empty" }
  | { kind: "hold"; scene: number }
  | { kind: "trans"; from: number; to: number; transition: TransitionKind; progress: number };

/** What is the swarm doing at `time` seconds? (wraps for looping). */
export function sampleAt(tl: Timeline, time: number): PlayState {
  const { segments, total } = layout(tl);
  if (segments.length === 0) return { kind: "empty" };
  const t = total > 0 ? ((time % total) + total) % total : 0;
  for (const s of segments) {
    if (t >= s.start && t < s.end) {
      if (s.type === "hold") return { kind: "hold", scene: s.index };
      const p = s.end > s.start ? (t - s.start) / (s.end - s.start) : 1;
      return { kind: "trans", from: s.from, to: s.to, transition: s.kind, progress: p };
    }
  }
  const last = segments[segments.length - 1];
  return last.type === "hold" ? { kind: "hold", scene: last.index } : { kind: "hold", scene: last.to };
}

type RawScene = { source: Source; hold?: number; look?: SceneLook };
type RawTrans = { kind?: string; duration?: number };

/** Serialise to a portable show file (drops runtime ids). */
export function toJSON(tl: Timeline): string {
  return JSON.stringify(
    {
      version: 2,
      kind: "swarm-timeline",
      scenes: tl.scenes.map((s) => ({ source: s.source, hold: s.hold, look: s.look })),
      transitions: tl.transitions.map((t) => ({ kind: t.kind, duration: t.duration })),
    },
    null,
    2
  );
}

/** Parse a saved show file back into a Timeline (fresh ids). Version-1 files
 * (no `look`) load with native colours. Throws on garbage. */
export function fromJSON(text: string): Timeline {
  const o = JSON.parse(text) as { scenes?: RawScene[]; transitions?: RawTrans[] };
  if (!o || !Array.isArray(o.scenes) || o.scenes.length === 0) {
    throw new Error("Not a swarm timeline (no scenes).");
  }
  const okLook = (l?: SceneLook): SceneLook =>
    l && (l.color === "solid" || l.color === "gradient" || l.color === "source") ? l : DEFAULT_LOOK;
  const scenes = o.scenes.map((s) => ({
    id: newSceneId(), source: s.source, hold: Number(s.hold) || 3, look: okLook(s.look),
  }));
  const transitions = (o.transitions ?? []).map((t) => ({
    kind: (BY_KIND.has(t.kind as TransitionKind) ? (t.kind as TransitionKind) : "morph") as TransitionKind,
    duration: Number(t.duration) || 1.5,
  }));
  return { scenes, transitions };
}
